/* @jsx h */
import { defineComponent, h, computed, PropType } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { formatPriceInLocale } from "@lemmonhq/orders";
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
  useLocalization,
} from "@lemmonhq/uikit";
import { useAuth, useTrpc } from "@app/vue/providers/index";

type CategoryRevenue = {
  category_id: number;
  category_name: string;
  color: string | null;
  gross_revenue: number;
  discounts: number;
  promotions: number;
  net_revenue: number;
  net_vat: number;
  items: number;
  product_count: number;
};

export const RevenueTab = defineComponent({
  name: "ChartRevenueTab",
  props: {
    from: { type: String, required: true },
    to: { type: String, required: true },
    onSelectCategory: {
      type: Function as PropType<(categoryId: number) => void>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";

    const summaryQuery = useQuery({
      queryKey: computed(() => [
        "accounting",
        "rev-summary",
        props.from,
        props.to,
      ]),
      queryFn: () =>
        trpc.accounting.revenueSummary.query({
          from: props.from,
          to: props.to,
        }),
    });

    const categoryQuery = useQuery({
      queryKey: computed(() => [
        "accounting",
        "rev-category",
        props.from,
        props.to,
      ]),
      queryFn: () =>
        trpc.accounting.revenueByCategory.query({
          from: props.from,
          to: props.to,
        }),
    });

    const categories = computed<CategoryRevenue[]>(
      () => categoryQuery.data.value ?? [],
    );
    // Gross revenue is the reliably product-attributable metric. Order-level
    // discounts/promotions are not allocated to items, so they are summarised at
    // restaurant level in the KPI cards rather than per product/category.
    const maxGross = computed(() =>
      Math.max(1, ...categories.value.map((c) => c.gross_revenue)),
    );

    const stats = computed(() => {
      const s = summaryQuery.data.value;
      if (!s) return [];
      return [
        {
          key: "net_revenue",
          label: t("reports.coa.rev_net_revenue"),
          value: s.net_revenue,
          icon: "trending_up",
          accent: "text-success",
          primary: true,
        },
        {
          key: "gross_revenue",
          label: t("reports.coa.rev_gross_revenue"),
          value: s.gross_revenue,
          icon: "payments",
        },
        {
          key: "net_vat",
          label: t("reports.coa.rev_net_vat"),
          value: s.net_vat,
          icon: "receipt_long",
        },
        {
          key: "discounts",
          label: t("reports.coa.rev_discounts"),
          value: s.discounts,
          icon: "sell",
          accent: "text-warning",
        },
        {
          key: "promotions",
          label: t("reports.coa.rev_promotions"),
          value: s.promotions,
          icon: "redeem",
          accent: "text-warning",
        },
        {
          key: "returns",
          label: t("reports.coa.rev_returns"),
          value: s.returns,
          icon: "undo",
          accent: "text-error",
        },
        {
          key: "tips",
          label: t("reports.coa.rev_tips"),
          value: s.tips,
          icon: "volunteer_activism",
        },
        {
          key: "gift_card_issued",
          label: t("reports.coa.rev_gift_card_issued"),
          value: s.gift_card_issued,
          icon: "card_giftcard",
        },
      ];
    });

    const columns = computed<ResponsiveTableColumn[]>(() => [
      {
        key: "category",
        label: t("reports.coa.col_category"),
        render: (row: CategoryRevenue) => (
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="inline-block h-3 w-3 rounded-full shrink-0 border border-base-content/10"
              style={{ background: row.color || "hsl(var(--p))" }}
            />
            <span class="font-medium text-base-content truncate">
              {row.category_name}
            </span>
          </div>
        ),
        mobilePriority: "primary",
      },
      {
        key: "products",
        label: t("reports.coa.col_products"),
        cellClass: "tabular-nums text-base-content/70",
        render: (row: CategoryRevenue) => row.product_count,
        mobilePriority: "secondary",
      },
      {
        key: "items",
        label: t("reports.coa.col_items"),
        cellClass: "tabular-nums text-base-content/70",
        render: (row: CategoryRevenue) => row.items.toLocaleString(),
        mobilePriority: "secondary",
      },
      {
        key: "share",
        label: t("reports.coa.col_share"),
        desktopHide: false,
        render: (row: CategoryRevenue) => {
          const pct = (row.gross_revenue / maxGross.value) * 100;
          return (
            <div class="w-32 h-2 rounded-full bg-base-300/60 overflow-hidden">
              <div
                class="h-full rounded-full bg-primary"
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </div>
          );
        },
        mobilePriority: "hidden",
      },
      {
        key: "net_revenue",
        label: t("reports.coa.rev_net_revenue"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums",
        render: (row: CategoryRevenue) => (
          <span class="font-semibold text-base-content">
            {formatPriceInLocale(row.net_revenue, true, 2, currency())}
          </span>
        ),
        mobilePriority: "primary",
        mobilePrimaryClass: "text-right shrink-0",
      },
    ]);

    return () => (
      <div class="flex flex-col gap-5">
        {/* KPI grid */}
        {summaryQuery.isLoading.value ? (
          <div class="flex justify-center items-center py-12">
            <span class="loading loading-spinner loading-lg text-primary opacity-60" />
          </div>
        ) : (
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.value.map((s) => (
              <div
                key={s.key}
                class={`rounded-2xl border border-base-300/60 bg-base-100 p-4 ${
                  s.primary ? "ring-1 ring-primary/30" : ""
                }`}
              >
                <div class="flex items-center gap-2 text-base-content/45">
                  <span class="material-symbols-outlined text-base">
                    {s.icon}
                  </span>
                  <span class="text-[11px] font-semibold uppercase tracking-wider">
                    {s.label}
                  </span>
                </div>
                <div
                  class={`mt-1.5 text-xl font-semibold tabular-nums ${
                    s.accent ?? "text-base-content"
                  }`}
                >
                  {formatPriceInLocale(s.value, true, 2, currency())}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Category breakdown */}
        <section class="rounded-2xl border border-base-300/60 bg-base-100 overflow-hidden shadow-sm">
          <div class="flex items-center gap-2 px-4 py-3 bg-base-200/50 border-b border-base-300/50">
            <span class="material-symbols-outlined text-base text-primary">
              category
            </span>
            <h2 class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
              {t("reports.coa.revenue_by_category")}
            </h2>
            <span class="ml-auto text-xs text-base-content/40">
              {t("reports.coa.tap_to_drill")}
            </span>
          </div>
          {categoryQuery.isError.value ? (
            <div class="flex flex-col items-center justify-center py-12 text-error gap-2">
              <span class="material-symbols-outlined text-4xl opacity-60">
                error_outline
              </span>
              <p class="text-sm">{t("reports.coa.error_load")}</p>
            </div>
          ) : (
            <ResponsiveTable
              columns={columns.value}
              rows={categories.value}
              rowKey={(row: CategoryRevenue) => row.category_id}
              loading={categoryQuery.isLoading.value}
              emptyMessage={t("reports.coa.empty_revenue")}
              onRowClick={(row: CategoryRevenue) =>
                props.onSelectCategory(row.category_id)
              }
            />
          )}
        </section>
      </div>
    );
  },
});
